<template>
  <a-card class="mx-2 quote-search">
    <v-form @submit.prevent="updateSearch">
      <v-row dense align="center" class="quote-search__row">
        <v-col cols="12" md="4">
          <elwrapper label="Title" midtext>
            <v-combobox
              v-model="keywords"
              multiple
              chips
              small-chips
              deletable-chips
              clearable
              dense
              hide-details
              outlined
              placeholder="Keywords…"
              :items="[]"
              :delimiters="[',']"
              class="quote-search__control"
              @keydown.enter.native="onKeywordEnter"
            />
          </elwrapper>
        </v-col>
        <v-col cols="12" md="4"><ClientAutocomplete v-model="client" /></v-col>
        <v-col cols="12" md="4">
          <a-text-field label="Reference" v-model="reference" midtext />
        </v-col>
      </v-row>

      <v-row dense align="center" class="quote-search__row">
        <v-col cols="12" md="4">
          <elwrapper label="Cities" midtext>
            <div class="quote-search__pair">
              <f-text v-model="cityStart" placeholder="Start" />
              <span class="quote-search__pair-sep">–</span>
              <f-text v-model="cityEnd" placeholder="End" />
            </div>
          </elwrapper>
        </v-col>
        <v-col cols="6" md="2">
          <elwrapper label="Year" midtext>
            <year-picker v-model="year" />
          </elwrapper>
        </v-col>
        <v-col cols="6" md="2">
          <elwrapper label="Nights" midtext>
            <f-number v-model="numberOfNights" full-width />
          </elwrapper>
        </v-col>
        <v-col cols="12" md="4">
          <elwrapper label="Favorite" midtext>
            <f-dropdown v-model="favorite" :items="favoriteOptions" />
          </elwrapper>
        </v-col>
      </v-row>

      <v-row dense align="center" class="quote-search__row">
        <v-col cols="12" md="6">
          <a-text-field label="Supplier" v-model="supplier" midtext />
        </v-col>
        <v-col cols="12" md="6">
          <a-text-field label="Supp. City" v-model="city" midtext />
        </v-col>
      </v-row>

      <div class="d-flex mt-2 align-center">
        <g-button label="Clear" @onclick="clearSearch" />
        <v-spacer />
        <search-button />
      </div>
    </v-form>
  </a-card>
</template>

<script>
import { ClientAutocomplete } from "@/modules/clients";

export default {
  components: {
    ClientAutocomplete,
  },
  data() {
    return {
      keywords: [],
      client: null,
      reference: "",
      cityStart: "",
      cityEnd: "",
      year: "",
      supplier: "",
      city: "",
      numberOfNights: "",
      favorite: null,
    };
  },
  computed: {
    favoriteOptions() {
      return [
        { label: "Non-favorite", value: "0" },
        { label: "Semi-favorite", value: "1" },
        { label: "Favorite", value: "2" },
        { label: "Reference", value: "3" },
      ];
    },
    normalizedKeywords() {
      return this.normalizeKeywords(this.keywords);
    },
  },
  mounted() {
    this.emitLayout();
  },
  methods: {
    emitLayout() {
      this.$nextTick(() => this.$emit("layout"));
    },
    normalizeKeywords(value) {
      const list = Array.isArray(value) ? value : [];
      const seen = new Set();
      const keywords = [];

      list.forEach((item) => {
        const text = String(item == null ? "" : item)
          .trim()
          .replace(/\s+/g, " ");
        if (!text) return;
        const key = text.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        keywords.push(text);
      });

      return keywords;
    },
    onKeywordEnter() {
      this.$nextTick(() => {
        this.keywords = this.normalizeKeywords(this.keywords);
      });
    },
    clearSearch() {
      this.keywords = [];
      this.client = null;
      this.reference = "";
      this.cityStart = "";
      this.cityEnd = "";
      this.year = "";
      this.supplier = "";
      this.city = "";
      this.numberOfNights = "";
      this.favorite = null;
      this.$emit("search", {});
      this.emitLayout();
    },
    updateSearch() {
      const keywords = this.normalizedKeywords;
      if (keywords.length !== (this.keywords || []).length) {
        this.keywords = keywords;
      }

      const searchData = {
        ...(keywords.length && { keywords }),
        ...(this.client && { client: this.client }),
        ...(this.reference && { reference: this.reference }),
        ...(this.cityStart && { city_start: this.cityStart }),
        ...(this.cityEnd && { city_end: this.cityEnd }),
        ...(this.year && { year: this.year }),
        ...(this.supplier && { supplier: this.supplier }),
        ...(this.city && { city: this.city }),
        ...(this.numberOfNights && { number_of_nights: this.numberOfNights }),
        ...(this.favorite !== null && this.favorite !== "" && { favorite: this.favorite }),
      };
      this.$emit("search", searchData);
      this.emitLayout();
    },
  },
};
</script>

<style lang="scss">
.quote-search {
  .quote-search__row {
    min-height: 44px;
  }

  // Same label column width on every field in this panel
  .a-field > .label,
  .a-field > .label.smlabel,
  .a-field > .label.widelabel,
  .a-field > .label.largelabel {
    flex: 0 0 72px !important;
    min-width: 72px !important;
    max-width: 72px !important;
  }

  .a-field {
    align-items: center;
    width: 100%;
  }

  .a-field > .col.py-0:last-child {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
  }

  .select-wrapper {
    width: 100%;

    .v-input {
      flex: 1 1 auto;
      min-width: 0;
    }

    .select-clear {
      margin-left: 4px;
      flex: 0 0 auto;
    }
  }

  .v-text-field--outlined.v-input--dense .v-input__slot,
  .v-select.v-text-field--outlined.v-input--dense .v-input__slot {
    min-height: 40px !important;
  }

  .v-text-field--outlined fieldset {
    border-width: 1px;
  }

  .quote-search__pair {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;

    .v-input {
      flex: 1 1 0;
      min-width: 0;
    }
  }

  .quote-search__pair-sep {
    flex: 0 0 auto;
    color: rgba(0, 0, 0, 0.45);
    font-weight: 600;
    line-height: 1;
  }

  .quote-search__control {
    .v-select__selections {
      min-height: 32px;
      padding-top: 2px !important;
      padding-bottom: 2px !important;
    }
  }
}
</style>
